import {
  AdditiveBlending,
  DynamicDrawUsage,
  GLSL3,
  InstancedBufferAttribute,
  InstancedBufferGeometry,
  Mesh,
  PlaneGeometry,
  Scene,
  ShaderMaterial,
  Vector2,
  type IUniform,
  type Texture,
} from 'three';
import type { NightLight } from '../../world/types';
import { painterWarpDefines, type SharedUniforms } from '../materials/sharedUniforms';
import { ANCHOR } from '../anchorCode';
import { NIGHT } from './nightLights';

import glowVert from '../shaders/glow.vert?raw';
import glowFrag from '../shaders/glow.frag?raw';

export interface GlowUniforms {
  tGeom: IUniform<Texture | null>;
  uResolution: IUniform<Vector2>;
  uTime: IUniform<number>;
  uNight: IUniform<number>;
  uBurning: IUniform<number>;
  uMinRadiusPx: IUniform<number>;
  uMinGain: IUniform<number>;
}

export interface GlowLayer {
  readonly scene: Scene;
  readonly uniforms: GlowUniforms;
  /**
   * The lights to draw from now on; the layer draws the first NIGHT.glows of them. A light that is
   * the same object in the same place of the list as the frame before is not written again.
   */
  setLights(lights: readonly NightLight[]): void;
  readonly count: number;
  dispose(): void;
}

/*
 * The painted pass cannot show a flame: the paint filter washes out anything under two pixels, and
 * the night's exposure then takes what is left. The glows are drawn over the finished frame instead,
 * one quad each, hidden by hand against the G-buffer's distances since that frame has no depth.
 */
export function createGlowMaterial(shared: SharedUniforms): ShaderMaterial {
  const uniforms: GlowUniforms = {
    tGeom: { value: null },
    uResolution: { value: new Vector2(1, 1) },
    uTime: { value: 0 },
    uNight: { value: 0 },
    uBurning: { value: 1 },
    uMinRadiusPx: { value: NIGHT.minRadiusPx },
    uMinGain: { value: NIGHT.minGain },
  };
  const material = new ShaderMaterial({
    glslVersion: GLSL3,
    vertexShader: glowVert,
    fragmentShader: glowFrag,
    defines: painterWarpDefines(shared),
    uniforms: { ...shared, ...uniforms } as unknown as Record<string, IUniform>,
    blending: AdditiveBlending,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  });
  material.userData['ams'] = true;
  return material;
}

const ATTRIBUTES = [
  ['aPlace', 4],
  ['aTint', 4],
  ['aFace', 4],
  ['aRideOrigin', 3],
  ['aRideScale', 2],
  ['aFrom', 1],
] as const;

export function createGlowLayer(material: ShaderMaterial): GlowLayer {
  const quad = new PlaneGeometry(2, 2);
  const geometry = new InstancedBufferGeometry();
  geometry.setIndex(quad.getIndex());
  geometry.setAttribute('position', quad.getAttribute('position'));
  const arrays = new Map<string, InstancedBufferAttribute>();
  for (const [name, size] of ATTRIBUTES) {
    const attribute = new InstancedBufferAttribute(new Float32Array(NIGHT.glows * size), size);
    attribute.setUsage(DynamicDrawUsage);
    geometry.setAttribute(name, attribute);
    arrays.set(name, attribute);
  }
  /* The painter projection reads this of every vertex; an array keeps the value another program
     left at its location out of this one (materials/programAttributes.ts). */
  const none = new Float32Array(NIGHT.glows * 2);
  for (let i = 0; i < NIGHT.glows; i++) none[i * 2] = ANCHOR.none;
  geometry.setAttribute('aExaggeration', new InstancedBufferAttribute(none, 2));
  geometry.instanceCount = 0;

  const mesh = new Mesh(geometry, material);
  mesh.frustumCulled = false;
  mesh.name = 'night-glows';
  const scene = new Scene();
  scene.add(mesh);

  const write = (name: string, i: number, values: readonly number[]): void => {
    arrays.get(name)!.array.set(values, i * values.length);
  };
  const written: NightLight[] = [];
  return {
    scene,
    uniforms: material.uniforms as unknown as GlowUniforms,
    get count() {
      return geometry.instanceCount;
    },
    setLights(lights) {
      const count = Math.min(lights.length, NIGHT.glows);
      let first = count,
        last = -1;
      for (let i = 0; i < count; i++) {
        const light = lights[i]!;
        if (written[i] === light) continue;
        written[i] = light;
        first = Math.min(first, i);
        last = i;
        const origin = light.rides?.origin ?? light.position;
        write('aPlace', i, [...light.position, light.size]);
        write('aTint', i, [...light.colour, light.phase]);
        write('aFace', i, [...(light.outward ?? [0, 0, 0]), light.aspect ?? 1]);
        write('aRideOrigin', i, origin);
        write('aRideScale', i, light.rides?.scale ?? [1, 1]);
        write('aFrom', i, [light.from ?? 0]);
      }
      written.length = count;
      geometry.instanceCount = count;
      for (const attribute of arrays.values()) {
        attribute.clearUpdateRanges();
        if (last < first) continue;
        attribute.addUpdateRange(
          first * attribute.itemSize,
          (last - first + 1) * attribute.itemSize
        );
        attribute.needsUpdate = true;
      }
    },
    dispose() {
      geometry.dispose();
      quad.dispose();
    },
  };
}
